import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import InputText from 'shared/ui/Input/InputText';
import Select from 'shared/ui/Select/Select';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import {TagsFilterField} from 'entities/monitor';
import {IPortMonitorFiltersForm} from 'features/port/model/PortMonitor/IPortMonitorFiltersForm';
import {booleanToString} from 'shared/lib/filterHelper';
import {useSort} from 'shared/lib/hooks/useSort';
import {
    getPortMonitorsFilters,
    PORT_MONITORS_DEFAULT_SORT,
    PORT_MONITORS_ORDER_FIELDS,
} from 'features/port/lib/portFilters';

import styles from './PortMonitorFiltersForm.module.scss';

interface IPortMonitorFiltersFormProps {
    onSubmit: SubmitHandler<IPortMonitorFiltersForm>;
    button: React.ReactElement;
}

const PortMonitorFiltersForm: React.FC<IPortMonitorFiltersFormProps> = ({onSubmit, button}) => {
    const [searchParams] = useSearchParams();
    const filters = getPortMonitorsFilters(searchParams);
    const [sort] = useSort({defaultSort: PORT_MONITORS_DEFAULT_SORT, sortFields: PORT_MONITORS_ORDER_FIELDS});

    const {register, handleSubmit, control} = useForm<IPortMonitorFiltersForm>({
        mode: 'onSubmit',
        defaultValues: {
            status: filters.status,
            ok: booleanToString(filters.ok),
            tags: filters.tags ?? [],
        },
    });

    const statusOptions = [
        {label: `${t`Активен`}`, value: 'active'},
        {label: `${t`На паузе`}`, value: 'paused'},
    ];

    const okOptions = [
        {label: `${t`Исправен`}`, value: 'true'},
        {label: `${t`Инцидент`}`, value: 'false'},
    ];

    const sortOptions = [
        {label: `${t`Название`}`, value: 'name'},
        {label: `${t`IP`}`, value: 'ip'},
        {label: `${t`Порт`}`, value: 'port'},
        {label: `${t`Состояние`}`, value: 'status'},
        {label: `${t`Исправность`}`, value: 'ok'},
        {label: `${t`Интервал`}`, value: 'checkInterval'},
        {label: `${t`Последняя проверка`}`, value: 'lastCheck'},
        {label: `${t`Следующая проверка`}`, value: 'nextCheck'},
    ];

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError label={t`Название или его часть`}>
                    <InputText
                        register={register}
                        defaultValue={filters.name}
                        name="name"
                        placeholder=""
                        control={control}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`IP или его часть`}>
                    <InputText
                        register={register}
                        defaultValue={filters.ip}
                        name="ip"
                        placeholder=""
                        control={control}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Порт`}>
                    <InputText
                        register={register}
                        defaultValue={filters.port}
                        name="port"
                        placeholder=""
                        control={control}
                    />
                </FormGroupWithError>

                <TagsFilterField control={control}/>

                <FormGroupWithError label={t`Состояние`}>
                    <Select
                        control={control}
                        name="status"
                        data={statusOptions}
                        defaultValue={filters.status}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Исправность`}>
                    <Select
                        control={control}
                        name="ok"
                        data={okOptions}
                        defaultValue={booleanToString(filters.ok)}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Сортировка`}>
                    <OrderByControlled
                        control={control}
                        name="orderBy"
                        defaultValue={sort}
                        data={sortOptions}
                    />
                </FormGroupWithError>
            </ModalBodyWrapper>
            <FormGroup>
                {button}
            </FormGroup>
        </form>
    );
};

export default PortMonitorFiltersForm;
